/**
 * LoginCallback Component
 * Handles the redirect after Auth0 authentication
 */
import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth } from './AuthContext';

const LoginCallback = () => {
  const { isLoading, error, isBootstrapping } = useAuth();
  const [callbackError, setCallbackError] = useState(null);

  useEffect(() => {
    // The AuthContext automatically handles the callback
    // We just need to wait for it to complete and let App routing handle redirects
    if (!isLoading && error) {
      // Only handle explicit errors, let main app routing handle success/redirect
      setCallbackError(error);
    }
  }, [isLoading, error]);

  // Show error if there's an Auth0 error or callback error
  if (error || callbackError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        p={3}
      >
        <Alert severity="error" sx={{ mb: 2, maxWidth: 400 }}>
          <Typography variant="h6" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body2">
            {error ||
              callbackError ||
              'An error occurred during authentication. Please try again.'}
          </Typography>
        </Alert>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2, textAlign: 'center' }}
        >
          Redirecting you back to the login page...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
    >
      <CircularProgress size={60} sx={{ mb: 2 }} />
      <Typography variant="h6" color="text.secondary">
        {isBootstrapping
          ? 'Setting up your account...'
          : 'Completing authentication...'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {isBootstrapping
          ? 'Please wait while we configure your account and permissions.'
          : 'Please wait while we finish setting up your session.'}
      </Typography>
    </Box>
  );
};

export default LoginCallback;
